import type { CombinedError } from '@pypestream/api-services/urql';
// The settings barrel carries the design system, which unit tests of this module cannot load.
import {
  type ExistingItem,
  toExistingItem,
} from '@pypestream/ui-patterns/settings/existing-item';

/** Maestro's refusal codes, kept apart so each gets its own message. */
export type MaestroError =
  | 'stale'
  | 'name_taken'
  | 'reserved_prefix'
  | 'pypestream_key_name'
  | 'scope_not_in_org'
  | 'cross_project_move'
  | 'forbidden'
  | 'invalid'
  | 'unavailable';

export interface MaestroFailure {
  reason: MaestroError;
  /** On `name_taken`: the item that already holds the name, to name it in the message. */
  existing?: ExistingItem;
}

export class MaestroRequestError extends Error {
  constructor(
    readonly reason: MaestroError,
    readonly existing?: ExistingItem
  ) {
    super(reason);
  }
}

const MAESTRO_CODES: Partial<Record<string, MaestroError>> = {
  STALE: 'stale',
  NAME_TAKEN: 'name_taken',
  RESERVED_PREFIX: 'reserved_prefix',
  PYPESTREAM_KEY_NAME: 'pypestream_key_name',
  SCOPE_NOT_IN_ORG: 'scope_not_in_org',
  PYPESTREAM_KEY_FORBIDDEN: 'forbidden',
  CROSS_PROJECT_MOVE: 'cross_project_move',
  AUDIENCE_REQUIRED: 'invalid',
  INVALID_AUDIENCE: 'invalid',
};

export const reasonOf = (error: CombinedError | undefined): MaestroFailure => {
  const extensions = error?.graphQLErrors?.[0]?.extensions ?? {};
  const code = extensions.code as string | undefined;
  const maestroCode = extensions.maestroCode as string | undefined;
  const fromMaestro = maestroCode ? MAESTRO_CODES[maestroCode] : undefined;

  if (fromMaestro === 'name_taken') {
    const existing = toExistingItem(extensions.existing);
    return existing
      ? { reason: fromMaestro, existing }
      : { reason: fromMaestro };
  }
  if (fromMaestro) return { reason: fromMaestro };
  if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN')
    return { reason: 'forbidden' };
  if (code === 'BAD_USER_INPUT') return { reason: 'invalid' };
  return { reason: 'unavailable' };
};

export const reasonFor = (error: unknown): MaestroFailure =>
  error instanceof MaestroRequestError
    ? error.existing
      ? { reason: error.reason, existing: error.existing }
      : { reason: error.reason }
    : { reason: 'unavailable' };
